import React from 'react';
import { useTranslation } from 'react-i18next';

const ACCENT_COLORS = [
  { border: 'border-indigo-500', badge: 'bg-indigo-50 text-indigo-700', dot: 'bg-indigo-400' },
  { border: 'border-blue-500',   badge: 'bg-blue-50 text-blue-700',     dot: 'bg-blue-400'   },
  { border: 'border-violet-500', badge: 'bg-violet-50 text-violet-700', dot: 'bg-violet-400' },
  { border: 'border-cyan-500',   badge: 'bg-cyan-50 text-cyan-700',     dot: 'bg-cyan-400'   },
  { border: 'border-emerald-500',badge: 'bg-emerald-50 text-emerald-700',dot: 'bg-emerald-400'},
  { border: 'border-amber-500',  badge: 'bg-amber-50 text-amber-700',   dot: 'bg-amber-400'  },
];

const LearnPage = ({ title, sections = [], targetDir = 'ltr', nativeDir = 'ltr', targetTts }) => {
  const { t } = useTranslation();
  const speak = (text) => {
    if (!text || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = targetTts;
    window.speechSynthesis.speak(utterance);
  };
  return (
    <div className="page-shell">
      <div className="page-panel">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <span className="pill">{t('learn.title')}</span>
            <h1 className="page-title section-gap">
              {t('learn.basicsTitle', { lang: title })}
            </h1>
            <p className="page-subtitle">
              Essential words and phrases, with meanings in your native language.
            </p>
          </div>
        </div>

        <div className="learn-grid">
          {sections.map((section, idx) => {
            const accent = ACCENT_COLORS[idx % ACCENT_COLORS.length];
            return (
              <article key={section.heading} className={`learn-card border-l-4 ${accent.border}`}>
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <h2 dir={targetDir} className="text-sm font-black text-slate-800">
                      {section.heading}
                    </h2>
                    {section.nativeHeading
                      && section.nativeHeading !== section.heading
                      && (
                        <p dir={nativeDir} className="mt-0.5 text-xs font-semibold text-slate-500">
                          {section.nativeHeading}
                        </p>
                      )}
                  </div>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wider ${accent.badge}`}>
                    {t('learn.itemCount', { count: section.items.length })}
                  </span>
                </div>

                {section.note && (
                  <p className="mb-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-1.5 text-xs italic text-slate-500">
                    {section.note}
                  </p>
                )}

                <ul>
                  {section.items.map((item, i) => (
                    <li
                      key={item.key || i}
                      className={`mb-2 flex items-center gap-2.5 rounded-xl border px-3 py-2.5 ${
                        i % 2 === 0
                          ? `${accent.badge} border-current/10`
                          : 'border-slate-200 bg-slate-50/80'
                      }`}
                    >
                      <span className={`mt-2 h-1.5 w-1.5 shrink-0 rounded-full ${accent.dot}`} />
                      <span className="min-w-0 flex-1 text-sm leading-relaxed">
                        <strong dir={targetDir} className="block font-bold text-slate-900">{item.targetText}</strong>
                        <span dir={nativeDir} className="block text-xs text-slate-500">{item.nativeText}</span>
                        {item.pronunciation && (
                          <span dir={nativeDir} className="block text-xs italic text-indigo-600">
                            {item.pronunciation}
                          </span>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() => speak(item.targetText)}
                        className="shrink-0 rounded-full p-2 text-indigo-600 transition hover:bg-indigo-50"
                        aria-label={`Listen to ${item.targetText}`}
                        title="Listen"
                      >
                        <span aria-hidden="true">🔊</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LearnPage;
